import { Badge } from "@volca/ui/components/ui/badge";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import {
  qboBillUnreconciledNote,
  qboBillVendorHint,
} from "@/accounts-payable/quickbooks-bill";
import type { ApQboBillPayload } from "@/accounts-payable/types";
import { formatApDate } from "@/accounts-payable/utils";
import { QboBillLines } from "./qbo-bill-lines";
import {
  RECORD_CARD_CLASS,
  RecordField,
  RecordFieldGrid,
} from "./record-fields";

/**
 * The bill a `create_qbo_bill` proposes (or an `update_qbo_bill` rewrites
 * the live one to), in the same grey card as a
 * ServiceTitan record: the vendor (flagged when it is new, or a guess worth
 * checking), the expense account, the bill's number and dates, then its lines.
 */
export const QboBillRecord = ({ payload }: { payload: ApQboBillPayload }) => {
  const { vendor } = payload;
  const vendorHint = qboBillVendorHint(payload);
  const unreconciledNote = qboBillUnreconciledNote(payload);

  return (
    <Stack align="start" gap={4} className={cn("w-full", RECORD_CARD_CLASS)}>
      <RecordField label="QuickBooks vendor">
        <Stack align="start" gap={1}>
          <Stack direction="row" gap={1} align="center" className="flex-wrap">
            <Text className="wrap-break-word">{vendor.display_name}</Text>
            {vendor.mode === "create" && (
              <Badge variant="secondary" color="information" size="sm">
                New vendor
              </Badge>
            )}
          </Stack>
          {vendorHint && (
            <Text weight="semibold" variant="caption" className="text-warning">
              {vendorHint}
            </Text>
          )}
        </Stack>
      </RecordField>
      <RecordField label="Expense account">
        {payload.expense_account_name}
      </RecordField>
      <RecordFieldGrid>
        <RecordField label="Bill number">
          {payload.doc_number ?? "–"}
        </RecordField>
        <RecordField label="Bill date">
          {formatApDate(payload.txn_date)}
        </RecordField>
        <RecordField label="Due date">
          {formatApDate(payload.due_date)}
        </RecordField>
      </RecordFieldGrid>
      <QboBillLines
        lines={payload.lines ?? []}
        totalCents={payload.total_cents}
      />
      {unreconciledNote && (
        <Text variant="caption" tone="muted">
          {unreconciledNote}
        </Text>
      )}
    </Stack>
  );
};
